import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// 색상에서 팔레트를 자동 생성하는 헬퍼 함수
const generateColorPalette = (primaryColor) => {
  // 기본 색상에서 다양한 톤의 색상을 생성
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHex = (r, g, b) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  const lighten = (color, amount) => {
    const rgb = hexToRgb(color);
    if (!rgb) return color;
    
    return rgbToHex(
      Math.min(255, Math.floor(rgb.r + (255 - rgb.r) * amount)),
      Math.min(255, Math.floor(rgb.g + (255 - rgb.g) * amount)),
      Math.min(255, Math.floor(rgb.b + (255 - rgb.b) * amount))
    );
  };

  const darken = (color, amount) => {
    const rgb = hexToRgb(color);
    if (!rgb) return color;
    
    return rgbToHex(
      Math.max(0, Math.floor(rgb.r * (1 - amount))),
      Math.max(0, Math.floor(rgb.g * (1 - amount))),
      Math.max(0, Math.floor(rgb.b * (1 - amount)))
    );
  };

  return [
    primaryColor,
    lighten(primaryColor, 0.3),
    lighten(primaryColor, 0.5),
    lighten(primaryColor, 0.7),
    darken(primaryColor, 0.2)
  ];
};

// 사용 가능한 색상 테마 정의
const themeConfigs = {
  default: {
    name: '기본 (녹색)',
    palette: {
      primary: { main: '#2e7d32' },
      secondary: { main: '#f50057' },
      background: { default: '#f5f5f5' },
    },
    chart: {
      primary: '#2e7d32',
      secondary: '#81c784',
      accent: '#4caf50',
      colors: ['#2e7d32', '#66bb6a', '#81c784', '#a5d6a7', '#c8e6c9']
    }
  },
  blue: {
    name: '블루',
    palette: {
      primary: { main: '#1976d2' },
      secondary: { main: '#dc004e' },
      background: { default: '#f5f5f5' },
    },
    chart: {
      primary: '#1976d2',
      secondary: '#64b5f6',
      accent: '#2196f3',
      colors: ['#1976d2', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb']
    }
  },
  purple: {
    name: '퍼플',
    palette: {
      primary: { main: '#7b1fa2' },
      secondary: { main: '#f50057' },
      background: { default: '#f5f5f5' },
    },
    chart: {
      primary: '#7b1fa2',
      secondary: '#ba68c8',
      accent: '#9c27b0',
      colors: ['#7b1fa2', '#ab47bc', '#ba68c8', '#ce93d8', '#e1bee7']
    }
  },
  orange: {
    name: '오렌지',
    palette: {
      primary: { main: '#f57400' },
      secondary: { main: '#3f51b5' },
      background: { default: '#f5f5f5' },
    },
    chart: {
      primary: '#f57400',
      secondary: '#ffb74d',
      accent: '#ff9800',
      colors: ['#f57400', '#ff9800', '#ffb74d', '#ffcc02', '#ffe082']
    }
  },
  teal: {
    name: '틸',
    palette: {
      primary: { main: '#00695c' },
      secondary: { main: '#ff5722' },
      background: { default: '#f5f5f5' },
    },
    chart: {
      primary: '#00695c',
      secondary: '#4db6ac',
      accent: '#009688',
      colors: ['#00695c', '#26a69a', '#4db6ac', '#80cbc4', '#b2dfdb']
    }
  },
  red: {
    name: '레드',
    palette: {
      primary: { main: '#c62828' },
      secondary: { main: '#1976d2' },
      background: { default: '#f5f5f5' },
    },
    chart: {
      primary: '#c62828',
      secondary: '#e57373',
      accent: '#f44336',
      colors: ['#c62828', '#e53935', '#e57373', '#ef9a9a', '#ffcdd2']
    }
  },
  dark: {
    name: '다크',
    palette: {
      mode: 'dark',
      primary: { main: '#90caf9' },
      secondary: { main: '#f48fb1' },
      background: { 
        default: '#121212',
        paper: '#1e1e1e'
      },
      text: {
        primary: '#ffffff',
        secondary: '#b0b0b0'
      }
    },
    chart: {
      primary: '#90caf9',
      secondary: '#64b5f6',
      accent: '#42a5f5',
      colors: ['#90caf9', '#64b5f6', '#42a5f5', '#2196f3', '#1976d2']
    }
  }
};

const ThemeContext = createContext();

export const useCustomTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useCustomTheme must be used within a ThemeContextProvider');
  }
  return context;
};

export const ThemeContextProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('default');
  const [customThemes, setCustomThemes] = useState({});

  // localStorage에서 테마 설정 로드
  useEffect(() => {
    const savedTheme = localStorage.getItem('app-theme');
    const savedCustomThemes = localStorage.getItem('app-custom-themes');
    
    if (savedCustomThemes) {
      try {
        setCustomThemes(JSON.parse(savedCustomThemes));
      } catch (error) {
        // 커스텀 테마 로드 실패 시 기본값 사용
      }
    }
    
    if (savedTheme) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  // 모든 테마 (기본 + 커스텀) 합치기
  const allThemes = { ...themeConfigs, ...customThemes };

  // 테마 변경 시 localStorage에 저장
  const changeTheme = (themeName) => {
    if (allThemes[themeName]) {
      setCurrentTheme(themeName);
      localStorage.setItem('app-theme', themeName);
    }
  };

  // 커스텀 테마 생성
  const createCustomTheme = (name, primaryColor, secondaryColor = '#f50057', customChartColors = null) => {
    // 커스텀 차트 색상이 제공되지 않으면 자동 생성
    const chartColors = customChartColors || {
      primary: primaryColor,
      secondary: generateColorPalette(primaryColor)[1],
      accent: generateColorPalette(primaryColor)[4],
      colors: generateColorPalette(primaryColor)
    };

    const customTheme = {
      name: `커스텀: ${name}`,
      palette: {
        primary: { main: primaryColor },
        secondary: { main: secondaryColor },
        background: { default: '#f5f5f5' },
      },
      chart: chartColors,
      isCustom: true
    };

    const customKey = `custom_${Date.now()}`;
    const newCustomThemes = {
      ...customThemes,
      [customKey]: customTheme
    };

    setCustomThemes(newCustomThemes);
    localStorage.setItem('app-custom-themes', JSON.stringify(newCustomThemes));
    
    // 생성한 테마로 즉시 변경
    setCurrentTheme(customKey);
    localStorage.setItem('app-theme', customKey);

    return customKey;
  };

  // 커스텀 테마 편집
  const editCustomTheme = (themeKey, name, primaryColor, secondaryColor = '#f50057', customChartColors = null) => {
    if (customThemes[themeKey]) {
      // 커스텀 차트 색상이 제공되지 않으면 자동 생성
      const chartColors = customChartColors || {
        primary: primaryColor,
        secondary: generateColorPalette(primaryColor)[1],
        accent: generateColorPalette(primaryColor)[4],
        colors: generateColorPalette(primaryColor)
      };

      const updatedTheme = {
        name: `커스텀: ${name}`,
        palette: {
          primary: { main: primaryColor },
          secondary: { main: secondaryColor },
          background: { default: '#f5f5f5' },
        },
        chart: chartColors,
        isCustom: true
      };

      const newCustomThemes = {
        ...customThemes,
        [themeKey]: updatedTheme
      };

      setCustomThemes(newCustomThemes);
      localStorage.setItem('app-custom-themes', JSON.stringify(newCustomThemes));

      return themeKey;
    }
    return null;
  };

  // 커스텀 테마 삭제
  const deleteCustomTheme = (themeKey) => {
    if (customThemes[themeKey]) {
      const newCustomThemes = { ...customThemes };
      delete newCustomThemes[themeKey];
      
      setCustomThemes(newCustomThemes);
      localStorage.setItem('app-custom-themes', JSON.stringify(newCustomThemes));
      
      // 삭제된 테마가 현재 테마인 경우 기본 테마로 변경
      if (currentTheme === themeKey) {
        setCurrentTheme('default');
        localStorage.setItem('app-theme', 'default');
      }
    }
  };

  // 테마 내보내기
  const exportThemes = () => {
    const exportData = {
      version: '1.0',
      exportDate: new Date().toISOString(),
      currentTheme,
      baseThemes: themeConfigs,
      customThemes
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `themes-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // 테마 가져오기
  const importThemes = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const importData = JSON.parse(e.target.result);
          
          // 데이터 유효성 검사
          if (!importData.version || !importData.customThemes) {
            throw new Error('유효하지 않은 테마 파일 형식입니다.');
          }

          // 커스텀 테마만 가져오기
          const importedCustomThemes = importData.customThemes || {};
          
          // 기존 커스텀 테마와 병합 (중복 시 덮어쓰기 확인)
          const conflictThemes = Object.keys(importedCustomThemes).filter(key => 
            customThemes[key]
          );

          if (conflictThemes.length > 0) {
            const confirmMessage = `다음 테마들이 이미 존재합니다:\n${conflictThemes.map(key => importedCustomThemes[key].name).join(', ')}\n\n덮어쓰시겠습니까?`;
            
            if (!window.confirm(confirmMessage)) {
              resolve({ success: false, message: '가져오기가 취소되었습니다.' });
              return;
            }
          }

          // 커스텀 테마 병합
          const newCustomThemes = {
            ...customThemes,
            ...importedCustomThemes
          };

          setCustomThemes(newCustomThemes);
          localStorage.setItem('app-custom-themes', JSON.stringify(newCustomThemes));

          resolve({ 
            success: true, 
            message: `${Object.keys(importedCustomThemes).length}개의 테마를 성공적으로 가져왔습니다.`,
            imported: Object.keys(importedCustomThemes).length
          });
          
        } catch (error) {
          reject(new Error(`테마 파일 읽기 실패: ${error.message}`));
        }
      };

      reader.onerror = () => {
        reject(new Error('파일 읽기에 실패했습니다.'));
      };

      reader.readAsText(file);
    });
  };

  // 현재 테마 설정으로 MUI 테마 생성
  const currentThemeConfig = allThemes[currentTheme] || themeConfigs.default;
  const muiTheme = createTheme({
    palette: currentThemeConfig.palette,
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontSize: '2.2rem', fontWeight: 600 },
      h2: { fontSize: '1.8rem', fontWeight: 500 },
      h3: { fontSize: '1.5rem', fontWeight: 500 },
      h4: { fontSize: '1.3rem', fontWeight: 500 },
      h5: { fontSize: '1.1rem', fontWeight: 500 },
      h6: { fontSize: '1rem', fontWeight: 500 },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            textTransform: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
  });

  const value = {
    currentTheme,
    themes: allThemes,
    baseThemes: themeConfigs,
    customThemes,
    changeTheme,
    createCustomTheme,
    editCustomTheme,
    deleteCustomTheme,
    exportThemes,
    importThemes,
    chartColors: currentThemeConfig.chart,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;